<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  notification: { type: Object, required: true },
});

const emit = defineEmits(['read', 'remove']);

const CATEGORY_META = {
  reminder: { icon: 'i-lucide-bell', color: 'text-n-amber-11', label: 'Recordatorio' },
  event: { icon: 'i-lucide-calendar', color: 'text-n-blue-11', label: 'Evento' },
  chat: { icon: 'i-lucide-message-circle', color: 'text-n-teal-11', label: 'Chat' },
  label: { icon: 'i-lucide-tag', color: 'text-n-ruby-11', label: 'Etiqueta' },
  system: { icon: 'i-lucide-settings', color: 'text-n-slate-11', label: 'Sistema' },
};

const EXECUTOR_META = {
  user: { icon: 'i-lucide-user-round', label: 'Usuario' },
  automation: { icon: 'i-lucide-bot', label: 'Automatización' },
  contact: { icon: 'i-lucide-contact', label: 'Contacto' },
  system: { icon: 'i-lucide-cpu', label: 'Sistema' },
};

const category = computed(
  () => CATEGORY_META[props.notification.category] || CATEGORY_META.system
);
const executor = computed(
  () =>
    EXECUTOR_META[props.notification.executor?.type] || EXECUTOR_META.system
);

const timeAgo = computed(() => {
  const diff = Date.now() - props.notification.timestamp;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'hace instantes';
  const min = Math.round(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `hace ${hr} h`;
  const day = Math.round(hr / 24);
  if (day < 30) return `hace ${day} d`;
  return new Date(props.notification.timestamp).toLocaleDateString('es');
});
</script>

<template>
  <div
    class="flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-n-alpha-1"
    :class="{ 'bg-n-alpha-1/60': !notification.read }"
    @click="emit('read', notification.id)"
  >
    <!-- Category icon -->
    <span
      class="grid flex-shrink-0 mt-0.5 size-8 rounded-lg place-content-center bg-n-alpha-2"
    >
      <span :class="[category.icon, category.color, 'size-4']" />
    </span>

    <!-- Body -->
    <div class="flex flex-col flex-1 min-w-0 gap-0.5">
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ notification.title }}
        </span>
        <span
          v-if="!notification.read"
          class="flex-shrink-0 rounded-full size-2 bg-n-brand"
        />
      </div>
      <span v-if="notification.body" class="text-sm text-n-slate-11 line-clamp-2">
        {{ notification.body }}
      </span>
      <div class="flex flex-wrap items-center gap-2 mt-1 text-xs text-n-slate-10">
        <span
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-n-alpha-2"
        >
          <span :class="[category.icon, 'size-3']" />
          {{ category.label }}
        </span>
        <span class="inline-flex items-center gap-1">
          <span :class="[executor.icon, 'size-3']" />
          {{ notification.executor?.name || executor.label }}
          <span class="text-n-slate-9">({{ executor.label }})</span>
        </span>
        <span>·</span>
        <span>{{ timeAgo }}</span>
      </div>
    </div>

    <!-- Remove -->
    <Button
      variant="ghost"
      color="slate"
      size="xs"
      icon="i-lucide-x"
      class="flex-shrink-0"
      @click.stop="emit('remove', notification.id)"
    />
  </div>
</template>
